import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { getIsEmptyObj } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import {
  HistoryPendingChangesActionType,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { getAgentConfig } from './AgentAndSeller.config';
import { TableDataType } from './AgentAndSeller.constants';
import {
  formContainerStyles,
  previousAddressFormControlsStyles,
  realtorPickerWrapperStyles,
} from './AgentAndSeller.styles';
import RealtorInviteButton from './RealtorInviteButton';
import RealtorPicker from './RealtorPicker';
import { getRealtorPrefillValues } from './RealtorPicker/RealtorPicker.utils';
import RealtorPortalAccessToggle from './RealtorPortalAccessToggle';

const SellingAgentForm = ({
  sellingAgentData,
  isLoading,
  cancelForm,
  pendingChanges,
  deleteSellingAgent,
  saveForm,
}) => {
  const dispatch = useDispatch();

  const isRealtorPickerEnabled = useFeatureToggle(
    FeatureFlag.EnableLoanContactsOnPresentationRealtors,
  );

  const [selectedRealtor, setSelectedRealtor] = useState(null);

  const observableFieldsNames = Object.keys(sellingAgentData);
  const isNewAdded =
    sellingAgentData.pendingChangesActionType === PendingChangesActionType.ADDED ||
    sellingAgentData.isAdded;

  const { initialFormValues, schema, fieldsConfig, config } = useMemo(() => {
    const config = getAgentConfig();

    const { initialFormValues, schema, fieldsConfig } = getConfigInfo({
      applicationData: sellingAgentData,
      config,
      isNestedForm: true,
    });

    return { initialFormValues, schema, fieldsConfig, config };
  }, [sellingAgentData]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const isFormEmpty = getIsEmptyObj(form);

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  const onRealtorSelect = useCallback(
    realtor => {
      setSelectedRealtor(realtor);

      Object.entries(getRealtorPrefillValues(realtor)).forEach(([field, value]) =>
        setInForm(field, value),
      );
    },
    [setInForm],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: sellingAgentData });
    },
    [form, validateForm, saveForm, sellingAgentData],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(sellingAgentData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(sellingAgentData);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, sellingAgentData]);

  const onClickRemove = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Remove Selling Agent',
        description: 'Are you sure you want to remove this selling agent?',
        onAccept: () => {
          deleteSellingAgent({ id: sellingAgentData.id, entityType: TableDataType.SELLING_AGENT });
        },
      }),
    );
  }, [deleteSellingAgent, dispatch, sellingAgentData.id]);

  return (
    <form
      noValidate
      data-ischanged={isFormChanged}
      data-testid={PropertyAndLoanTestIds.SELLING_AGENT_FORM}
      onSubmit={onSubmit}
    >
      <Box sx={formContainerStyles}>
        {isRealtorPickerEnabled && (
          <Box sx={realtorPickerWrapperStyles}>
            <RealtorPicker disabled={isLoading} onSelect={onRealtorSelect} />
            <RealtorPortalAccessToggle disabled={isLoading} realtor={selectedRealtor} />
            {!selectedRealtor && (
              <RealtorInviteButton
                disabled={isLoading}
                email={form.email}
                name={form.name}
                phone={form.phone}
              />
            )}
          </Box>
        )}
        <FormGenerator
          isNestedForm
          config={config}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.AGENT}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={3}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLING_AGENT_FORM_REMOVE_BUTTON}
            disabled={isNewAdded}
            size="small"
            startIcon={<DeleteIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={onClickRemove}
          >
            Remove Selling Agent
          </Button>
        </Box>
        <Box sx={previousAddressFormControlsStyles}>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLING_AGENT_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLING_AGENT_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged || isFormEmpty}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

SellingAgentForm.propTypes = {
  deleteSellingAgent: PropTypes.func.isRequired,
  sellingAgentData: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  ...WithLoadingPropTypes,
};

export default memo(SellingAgentForm);
